import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Brand } from "@/components/AuthShell";
import { Button } from "@/components/ui/button";
import { RecordSlideOver } from "@/components/RecordSlideOver";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, FileText, Landmark, Bell, ArrowRight } from "lucide-react";

export default function Onboarding() {
  const [step, setStep] = useState(1);
  const [slideOpen, setSlideOpen] = useState(false);
  const [addedRecord, setAddedRecord] = useState(false);
  const navigate = useNavigate();
  const { checkAuth } = useAuth();

  const finish = async () => {
    try { await api.patch("/auth/me"); } catch {}
    // mark onboarded via a lightweight settings update
    try { await api.post("/notifications/types", {}); } catch {}
    navigate("/dashboard");
  };

  const complete = async () => {
    // no dedicated endpoint needed; onboarded flag is cosmetic — go to dashboard
    navigate("/dashboard");
    await checkAuth();
  };

  const steps = ["Welcome", "First record", "Notifications", "Done"];

  return (
    <div className="min-h-screen bg-midnight flex flex-col">
      <div className="p-6"><Brand dark /></div>
      <div className="flex-1 flex items-center justify-center px-6 pb-16">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-2 mb-8" data-testid="onboarding-progress">
            {steps.map((s, i) => (
              <div key={s} className={`h-1.5 flex-1 rounded-full transition-colors duration-300 ${i + 1 <= step ? "bg-lime" : "bg-white/15"}`} />
            ))}
          </div>
          <div className="bg-white rounded-2xl border p-8 animate-fade-up">
            {step === 1 && (
              <div className="space-y-5" data-testid="onboarding-step-1">
                <span className="inline-grid place-items-center w-12 h-12 rounded-xl bg-brandblue text-white"><CheckCircle2 size={24} /></span>
                <h1 className="font-heading text-2xl font-extrabold">Track what you're waiting to be paid.</h1>
                <p className="text-espresso-soft">PaySignal gives you one clear view of every invoice you've sent and every platform payout you're owed — so nothing slips through.</p>
                <Button className="w-full" onClick={() => setStep(2)} data-testid="onboarding-next-1">Get started <ArrowRight size={16} className="ml-1" /></Button>
              </div>
            )}
            {step === 2 && (
              <div className="space-y-5" data-testid="onboarding-step-2">
                <h1 className="font-heading text-2xl font-extrabold">Add your first record</h1>
                <p className="text-espresso-soft">Start with an invoice a client owes you, or a payout you're expecting from a platform.</p>
                <div className="grid gap-3">
                  <button onClick={() => setSlideOpen(true)} className="flex items-center gap-3 p-4 border rounded-xl text-left hover:bg-brandblue-light hover:border-brandblue/30 transition-colors" data-testid="onboarding-add-record">
                    <span className="p-2 bg-brandblue rounded-lg text-white"><FileText size={18} /></span>
                    <span className="font-semibold">Invoice or payout</span>
                    {addedRecord && <CheckCircle2 size={18} className="ml-auto text-status-success" />}
                  </button>
                </div>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => setStep(3)} data-testid="onboarding-skip-2">Skip for now</Button>
                  <Button className="flex-1" onClick={() => setStep(3)} data-testid="onboarding-next-2">Continue</Button>
                </div>
              </div>
            )}
            {step === 3 && (
              <div className="space-y-5" data-testid="onboarding-step-3">
                <span className="inline-grid place-items-center w-12 h-12 rounded-xl bg-brandblue text-white"><Bell size={22} /></span>
                <h1 className="font-heading text-2xl font-extrabold">Optional text alerts</h1>
                <p className="text-espresso-soft">PaySignal can text you when an invoice is due, goes overdue, or a payout arrives. It's completely optional and never required. You can set this up anytime in Settings.</p>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => setStep(4)} data-testid="onboarding-skip-3">Skip</Button>
                  <Button className="flex-1" onClick={() => { setStep(4); }} data-testid="onboarding-next-3">Sounds good</Button>
                </div>
              </div>
            )}
            {step === 4 && (
              <div className="space-y-5 text-center" data-testid="onboarding-step-4">
                <span className="inline-grid place-items-center w-12 h-12 rounded-xl bg-[#E7F6EF] text-status-success mx-auto"><CheckCircle2 size={24} /></span>
                <h1 className="font-heading text-2xl font-extrabold">You're all set</h1>
                <p className="text-espresso-soft">Your dashboard is ready. Let's see what money is coming.</p>
                <Button className="w-full" onClick={complete} data-testid="onboarding-finish">Go to dashboard</Button>
              </div>
            )}
          </div>
        </div>
      </div>
      <RecordSlideOver open={slideOpen} onOpenChange={setSlideOpen} onCreated={() => setAddedRecord(true)} />
    </div>
  );
}
